/**
 * Theme Context for BOM Extractor
 * 
 * Provides theme management with support for:
 * - Indigo theme (modern indigo/purple)
 * - PP Control & Automation theme (blue/yellow/red)
 * 
 * Theme is persisted in localStorage and can be switched at runtime
 */

import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { Theme } from '@mui/material/styles';
import { themes, ThemeType } from '../themes';
import { useAuth } from './AuthContext';

interface ThemeContextType {
  currentTheme: ThemeType;
  theme: Theme;
  setTheme: (themeType: ThemeType) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useAppTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useAppTheme must be used within a ThemeContextProvider');
  }
  return context;
};

interface ThemeContextProviderProps {
  children: ReactNode;
}

export const ThemeContextProvider: React.FC<ThemeContextProviderProps> = ({ children }) => {
  const { user } = useAuth();
  
  // Get theme from user's current organization or default to 'ppcanda'
  // IMPORTANT: Always prefer organization theme over localStorage
  const [currentTheme, setCurrentTheme] = useState<ThemeType>(() => {
    // First check if user has a current organization with theme
    if (user?.current_organization?.theme) {
      const orgTheme = user.current_organization.theme as ThemeType;
      if (orgTheme === 'indigo' || orgTheme === 'ppcanda') {
        console.log('[ThemeContext] Using organization theme:', orgTheme);
        localStorage.setItem('bom-extractor-theme', orgTheme); // Sync localStorage
        return orgTheme;
      }
    }
    
    // Default to ppcanda (PP Canada is the primary customer)
    // Only use localStorage if it matches a valid theme AND we don't have user data yet
    if (!user) {
      const savedTheme = localStorage.getItem('bom-extractor-theme') as ThemeType;
      if (savedTheme && (savedTheme === 'indigo' || savedTheme === 'ppcanda')) {
        console.log('[ThemeContext] Using saved theme (no user yet):', savedTheme);
        return savedTheme;
      }
    }
    
    console.log('[ThemeContext] Using default theme: ppcanda');
    return 'ppcanda';
  });

  // Update theme when user's organization changes - ALWAYS use org theme when available
  useEffect(() => {
    if (user?.current_organization?.theme) {
      const orgTheme = user.current_organization.theme as ThemeType;
      if (orgTheme === 'indigo' || orgTheme === 'ppcanda') {
        // Force update if current theme doesn't match organization theme
        if (currentTheme !== orgTheme) {
          console.log('[ThemeContext] Theme updated from organization:', orgTheme, '(was:', currentTheme, ')');
          setCurrentTheme(orgTheme);
        }
        localStorage.setItem('bom-extractor-theme', orgTheme);
      }
    }
  }, [user?.current_organization_id, user?.current_organization?.theme, currentTheme]);

  // Update localStorage when theme changes (manual override)
  useEffect(() => {
    localStorage.setItem('bom-extractor-theme', currentTheme);
    console.log('[ThemeContext] Theme changed to:', currentTheme);
  }, [currentTheme]);

  const setTheme = (themeType: ThemeType) => {
    setCurrentTheme(themeType);
  };

  const toggleTheme = () => {
    setCurrentTheme(prev => prev === 'indigo' ? 'ppcanda' : 'indigo');
  };

  const theme = themes[currentTheme];

  const value: ThemeContextType = {
    currentTheme,
    theme,
    setTheme,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

